import { useMemo, useState, type ReactNode } from "react";
import s from "./Testing.module.css";
import { locale, t } from "../../i18n";

/*
 * Small SVG charts drawn from the data they are given, with a hover readout on
 * every mark and the numbers always available in a table beside them.
 *
 * Colour: the current series is the accent, the earlier one a neutral — the
 * comparison is "now against then", and then is context. Threshold levels are
 * drawn as labelled lines in the status colours, never as a series colour.
 */

const W = 640;
const H = 220;
const PAD = { top: 16, right: 12, bottom: 34, left: 44 };

function niceStep(span: number, target = 10): number {
  const raw = span / target;
  const pow = 10 ** Math.floor(Math.log10(raw || 1));
  const n = raw / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * pow;
}

const fmt = (v: number) =>
  Math.abs(v) >= 100
    ? Math.round(v).toLocaleString(locale())
    : v.toLocaleString(locale(), { maximumFractionDigits: 1 });

function Tooltip({ x, y, children }: { x: number; y: number; children: ReactNode }) {
  return (
    <div className={s.tip} style={{ left: `${(100 * x) / W}%`, top: `${(100 * y) / H}%` }} role="status">
      {children}
    </div>
  );
}

export interface Level {
  value: number;
  label: string;
  tone: "fail" | "attention";
}

/**
 * How the measured values are spread, now against the test before, with the
 * action and attention levels marked. One bar pair per band of values.
 */
export function DistributionChart({
  latest,
  prior,
  levels,
  unit,
  latestLabel,
  priorLabel,
}: {
  latest: number[];
  prior: number[];
  levels: Level[];
  unit: string;
  latestLabel: string;
  priorLabel?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const bins = useMemo(() => {
    const all = [...latest, ...prior, ...levels.map((l) => l.value)];
    const max = Math.max(1, ...all);
    const step = niceStep(max, 12);
    const count = Math.ceil((max + 1e-9) / step);
    const out = Array.from({ length: count }, (_, i) => ({ from: i * step, to: (i + 1) * step, latest: 0, prior: 0 }));
    const put = (v: number, k: "latest" | "prior") =>
      (out[Math.min(count - 1, Math.max(0, Math.floor(v / step)))][k] += 1);
    latest.forEach((v) => put(v, "latest"));
    prior.forEach((v) => put(v, "prior"));
    return { out, step, max: count * step };
  }, [latest, prior, levels]);

  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const yMax = Math.max(1, ...bins.out.map((b) => Math.max(b.latest, b.prior)));
  const x = (v: number) => PAD.left + (innerW * v) / bins.max;
  const y = (n: number) => PAD.top + innerH - (innerH * n) / yMax;
  const band = innerW / bins.out.length;
  const barW = Math.max(2, (band - 4) / (prior.length ? 2 : 1) - 1);
  const yTicks = niceStep(yMax, 4);

  return (
    <figure className={s.figure}>
      <div className={s.legend}>
        <span>
          <i className={s.swLatest} /> {latestLabel}
        </span>
        {prior.length > 0 && priorLabel && (
          <span>
            <i className={s.swPrior} /> {priorLabel}
          </span>
        )}
        {levels.map((l) => (
          <span key={l.label}>
            <i className={l.tone === "fail" ? s.swFail : s.swAttention} /> {l.label}
          </span>
        ))}
      </div>
      <div className={s.chartWrap} dir="ltr">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className={s.svg}
          role="img"
          aria-label={
            priorLabel
              ? t("Distribution of {unit}, {latest} against {prior}", { unit, latest: latestLabel, prior: priorLabel })
              : t("Distribution of {unit}, {latest}", { unit, latest: latestLabel })
          }
        >
          {Array.from({ length: Math.floor(yMax / yTicks) + 1 }, (_, i) => i * yTicks).map((tick) => (
            <g key={tick}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} className={s.grid} />
              <text x={PAD.left - 6} y={y(tick) + 3} className={s.axisText} textAnchor="end">
                {tick}
              </text>
            </g>
          ))}
          {bins.out.map((b, i) => {
            const x0 = PAD.left + i * band + 2;
            return (
              <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <rect x={PAD.left + i * band} y={PAD.top} width={band} height={innerH} fill="transparent" />
                {prior.length > 0 && b.prior > 0 && (
                  <rect
                    x={x0}
                    y={y(b.prior)}
                    width={barW}
                    height={PAD.top + innerH - y(b.prior)}
                    rx={2}
                    className={s.barPrior}
                  />
                )}
                {b.latest > 0 && (
                  <rect
                    x={prior.length ? x0 + barW + 1 : x0}
                    y={y(b.latest)}
                    width={barW}
                    height={PAD.top + innerH - y(b.latest)}
                    rx={2}
                    className={s.barLatest}
                  />
                )}
              </g>
            );
          })}
          {levels.map((l) => (
            <g key={l.label}>
              <line
                x1={x(l.value)}
                x2={x(l.value)}
                y1={PAD.top}
                y2={PAD.top + innerH}
                className={l.tone === "fail" ? s.levelFail : s.levelAttention}
              />
              <text x={x(l.value) + 3} y={PAD.top + 9} className={s.levelText}>
                {fmt(l.value)}
              </text>
            </g>
          ))}
          {bins.out
            .filter((_, i) => i % Math.ceil(bins.out.length / 8) === 0)
            .map((b) => (
              <text key={b.from} x={x(b.from)} y={H - PAD.bottom + 14} className={s.axisText} textAnchor="middle">
                {fmt(b.from)}
              </text>
            ))}
          <text x={PAD.left + innerW / 2} y={H - 4} className={s.axisText} textAnchor="middle">
            {unit}
          </text>
          <line x1={PAD.left} x2={W - PAD.right} y1={PAD.top + innerH} y2={PAD.top + innerH} className={s.axis} />
        </svg>
        {hover != null && (
          <Tooltip x={PAD.left + (hover + 0.5) * band} y={PAD.top + 8}>
            <b>
              {fmt(bins.out[hover].from)}–{fmt(bins.out[hover].to)} {unit}
            </b>
            <div>
              {latestLabel}: {bins.out[hover].latest}
            </div>
            {prior.length > 0 && priorLabel && (
              <div>
                {priorLabel}: {bins.out[hover].prior}
              </div>
            )}
          </Tooltip>
        )}
      </div>
    </figure>
  );
}

export interface TrendPoint {
  period: string;
  value: number;
  average: number | null;
}

/** A measure over time: each period's value, and its trailing average as the trend. */
export function TrendChart({
  points,
  unit,
  averageLabel,
}: {
  points: TrendPoint[];
  unit: string;
  averageLabel: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  if (points.length < 2) return <div className={s.empty}>{t("Not enough readings to draw a trend.")}</div>;
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const max = Math.max(...points.map((p) => Math.max(p.value, p.average ?? 0))) * 1.08 || 1;
  const step = niceStep(max, 4);
  const top = Math.ceil(max / step) * step;
  const x = (i: number) => PAD.left + (innerW * i) / (points.length - 1);
  const y = (v: number) => PAD.top + innerH - (innerH * v) / top;
  const line = points.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join("");
  const avg = points
    .map((p, i) => (p.average == null ? null : `${x(i).toFixed(1)},${y(p.average).toFixed(1)}`))
    .filter(Boolean)
    .map((pt, i) => `${i ? "L" : "M"}${pt}`)
    .join("");
  const labelEvery = Math.ceil(points.length / 6);

  return (
    <figure className={s.figure}>
      <div className={s.legend}>
        <span>
          <i className={s.swPrior} /> {t("Each day")}
        </span>
        <span>
          <i className={s.swLatest} /> {averageLabel}
        </span>
      </div>
      <div className={s.chartWrap} dir="ltr">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className={s.svg}
          role="img"
          aria-label={t("{unit} per day with {average}", { unit, average: averageLabel })}
          onMouseLeave={() => setHover(null)}
          onMouseMove={(e) => {
            const box = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
            const vx = ((e.clientX - box.left) / box.width) * W;
            setHover(
              Math.max(0, Math.min(points.length - 1, Math.round(((vx - PAD.left) / innerW) * (points.length - 1)))),
            );
          }}
        >
          {Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step).map((tick) => (
            <g key={tick}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(tick)} y2={y(tick)} className={s.grid} />
              <text x={PAD.left - 6} y={y(tick) + 3} className={s.axisText} textAnchor="end">
                {fmt(tick)}
              </text>
            </g>
          ))}
          <path d={line} className={s.lineDaily} />
          <path d={avg} className={s.lineTrend} />
          {points.map((p, i) =>
            i % labelEvery === 0 ? (
              <text key={p.period} x={x(i)} y={H - PAD.bottom + 14} className={s.axisText} textAnchor="middle">
                {p.period.slice(5)}
              </text>
            ) : null,
          )}
          <text x={PAD.left + innerW / 2} y={H - 4} className={s.axisText} textAnchor="middle">
            {t("{unit} per day", { unit })}
          </text>
          {hover != null && (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + innerH} className={s.crosshair} />
              <circle cx={x(hover)} cy={y(points[hover].value)} r={4} className={s.dotDaily} />
              {points[hover].average != null && (
                <circle cx={x(hover)} cy={y(points[hover].average!)} r={4} className={s.dotTrend} />
              )}
            </g>
          )}
          <line x1={PAD.left} x2={W - PAD.right} y1={PAD.top + innerH} y2={PAD.top + innerH} className={s.axis} />
        </svg>
        {hover != null && (
          <Tooltip x={x(hover)} y={PAD.top + 8}>
            <b>{points[hover].period}</b>
            <div>
              {fmt(points[hover].value)} {unit}
            </div>
            {points[hover].average != null && (
              <div>
                {averageLabel}: {fmt(points[hover].average!)} {unit}
              </div>
            )}
          </Tooltip>
        )}
      </div>
    </figure>
  );
}

/** Change between two windows per item, as a diverging bar from zero. */
export function ChangeChart({ rows }: { rows: { label: string; change: number | null; detail: string }[] }) {
  const shown = rows.filter((r) => r.change != null);
  if (shown.length === 0)
    return <div className={s.empty}>{t("No meter has readings in both windows, so no change can be worked out.")}</div>;
  const span = Math.max(10, ...shown.map((r) => Math.abs(r.change!)));
  return (
    <ul className={s.changeList}>
      {rows.map((r) => (
        <li key={r.label} className={s.changeRow} title={r.detail}>
          <span className={s.changeLabel}>{r.label}</span>
          <span className={s.changeTrack} dir="ltr">
            <span className={s.changeZero} />
            {r.change != null && (
              <span
                className={r.change > 0 ? s.changeUp : s.changeDown}
                style={
                  r.change > 0
                    ? { left: "50%", width: `${(50 * r.change) / span}%` }
                    : { right: "50%", width: `${(50 * -r.change) / span}%` }
                }
              />
            )}
          </span>
          <span className={s.changeValue}>
            {r.change == null ? "—" : `${r.change > 0 ? "+" : ""}${r.change.toFixed(1)}%`}
          </span>
        </li>
      ))}
    </ul>
  );
}
